import React, { type ReactElement, type ReactNode, useMemo } from 'react';

import AvatarGroup, { type AvatarGroupProps, type AvatarProps } from '@atlaskit/avatar-group';
import Heading from '@atlaskit/heading';
// eslint-disable-next-line @atlaskit/design-system/no-emotion-primitives -- to be migrated to @atlaskit/primitives/compiled – go/akcss
import { Box, Inline, type InlineProps, Pressable, Stack, Text, xcss } from '@atlaskit/primitives';
import { token, useThemeObserver } from '@atlaskit/tokens';

import usedAvatarDarkSrc from './assets/user-brand-dark.svg';
import usedAvatarLightSrc from './assets/user-brand-light.svg';
import unusedAvatarDarkSrc from './assets/user-gray-dark.svg';
import unusedAvatarLightSrc from './assets/user-gray-light.svg';

const dividerContainerStyles = xcss({
	position: 'relative',
	overflow: 'visible',
});

const dividerStyles = xcss({
	height: '48px',
	marginLeft: 'space.075',
	paddingLeft: 'space.050',
	marginRight: 'space.150',
	borderLeftWidth: 'border.width.selected',
	borderLeftStyle: 'solid',
	borderLeftColor: 'color.border',
});

const dividerStyleShort = xcss({
	height: '32px',
});

const dividerTextStyles = xcss({
	position: 'absolute',
	paddingLeft: 'space.050',
	bottom: 'space.negative.025',
	left: 'space.600',
	width: 'max-content',
});

const indicator = xcss({
	minWidth: '32px',
	height: '32px',
	backgroundColor: 'color.background.accent.purple.subtler',
	borderRadius: 'radius.full',
	color: 'color.text.discovery',
	font: 'font.body.small',
	':hover': {
		backgroundColor: 'color.background.accent.purple.subtler.hovered',
	},
	':active': {
		backgroundColor: 'color.background.accent.purple.subtler.pressed',
	},
});

const indicatorActive = xcss({
	backgroundColor: 'color.background.accent.purple.subtlest',
	borderColor: 'color.border.discovery',
	borderWidth: 'border.width.selected',
	borderStyle: 'solid',
	':hover': {
		backgroundColor: 'color.background.accent.purple.subtlest.hovered',
	},
	':active': {
		backgroundColor: 'color.background.accent.purple.subtlest.pressed',
	},
});

const indicatorWarning = xcss({
	minWidth: '32px',
	height: '32px',
	borderRadius: 'radius.full',
	color: 'color.text.warning.inverse',
	font: 'font.body.small',
	backgroundColor: 'color.background.warning.bold',
	':hover': {
		backgroundColor: 'color.background.warning.bold.hovered',
	},
	':active': {
		backgroundColor: 'color.background.warning.bold.pressed',
	},
});

const indicatorActiveWarning = xcss({
	borderWidth: 'border.width.selected',
	borderStyle: 'solid',
	backgroundColor: 'color.background.warning.bold',
	borderColor: 'color.border.warning',
	':hover': {
		backgroundColor: 'color.background.warning.bold.hovered',
	},
	':active': {
		backgroundColor: 'color.background.warning.bold.pressed',
	},
});

const AVATAR_LIMIT_DEFAULT = 10;

const EMPTY_AVATAR: AvatarProps = {
	key: '',
	name: '',
};

type UserLimitsProps = {
	assignedUsers: AvatarProps[] | number;
	description: ReactNode;
	dividerLimit?: number;
	/** The <Text /> to display under the over limit indicator. */
	dividerLimitLabel?: ReactNode;
	headerIcon?: ReactElement;
	horizontalAlignment?: InlineProps['alignInline'];
	limit?: number;
	size?: AvatarGroupProps['size'];
	title: ReactNode;
	warningStyle?: boolean;
};

export const UserLimits = ({
	title,
	description,
	assignedUsers,
	limit,
	size,
	dividerLimit,
	headerIcon,
	dividerLimitLabel,
	warningStyle,
	horizontalAlignment,
}: UserLimitsProps): React.JSX.Element => {
	const { colorMode } = useThemeObserver();

	const avatars = useMemo(() => {
		const usedAvatarSrc = colorMode === 'dark' ? usedAvatarDarkSrc : usedAvatarLightSrc;
		const unusedAvatarSrc = colorMode === 'dark' ? unusedAvatarDarkSrc : unusedAvatarLightSrc;
		const usedAvatar = { ...EMPTY_AVATAR, src: usedAvatarSrc };
		const unusedAvatar = { ...EMPTY_AVATAR, src: unusedAvatarSrc };

		if (!Array.isArray(assignedUsers)) {
			const usedAvatars = new Array(assignedUsers).fill(usedAvatar);
			const unusedAvatars =
				limit && limit - assignedUsers > 0
					? new Array(limit - assignedUsers).fill(unusedAvatar)
					: [];
			return [...usedAvatars, ...unusedAvatars] as AvatarProps[];
		}

		if (limit === undefined || assignedUsers.length >= limit) {
			// No limit, just return the assigned users
			return assignedUsers;
		}

		const dummyAvatars = new Array(limit - assignedUsers.length).fill(unusedAvatar);

		return [...assignedUsers, ...dummyAvatars] as AvatarProps[];
	}, [assignedUsers, limit, colorMode]);

	return (
		<Stack space="space.150">
			<Stack space="space.050" alignInline={horizontalAlignment}>
				{headerIcon ? (
					<Inline alignBlock="center" space="space.100">
						{headerIcon}
						<Heading size="small">{title}</Heading>
					</Inline>
				) : (
					<Heading size="small">{title}</Heading>
				)}
				<Text size="small" color="color.text.subtlest">
					{description}
				</Text>
			</Stack>
			<Inline alignBlock="center" alignInline={horizontalAlignment}>
				{dividerLimit !== undefined ? (
					<AvatarGroup
						appearance="stack"
						data={avatars}
						maxCount={dividerLimit + 1}
						size={size ?? 'medium'}
						borderColor={token('color.border.inverse')}
						overrides={{
							Avatar: {
								render: (Component, props, index) =>
									index === dividerLimit - 1 && avatars.length > dividerLimit + 1 ? (
										<Inline xcss={dividerContainerStyles}>
											<Component {...props} key={index} />
											<Box xcss={[dividerStyles, !dividerLimitLabel && dividerStyleShort]}></Box>
											{dividerLimitLabel && (
												<Box xcss={dividerTextStyles}>
													<Text
														size="small"
														weight="medium"
														color={warningStyle ? 'color.text' : 'color.text.discovery'}
													>
														{dividerLimitLabel}
													</Text>
												</Box>
											)}
										</Inline>
									) : (
										<Component {...props} key={index} />
									),
							},
							MoreIndicator: {
								render: (Component, { buttonProps, borderColor, isActive, ...props }) => (
									<Pressable
										type="submit"
										{...buttonProps}
										{...props}
										isDisabled={!Array.isArray(assignedUsers)}
										xcss={[
											indicator,
											warningStyle && indicatorWarning,
											isActive && indicatorActive,
											isActive && warningStyle && indicatorActiveWarning,
										]}
										testId={props.testId && `${props.testId}-0`}
									>
										+{avatars.length - dividerLimit}
									</Pressable>
								),
							},
						}}
					/>
				) : (
					<AvatarGroup
						appearance="stack"
						data={avatars}
						maxCount={(limit ?? AVATAR_LIMIT_DEFAULT) + 1}
						size={size ?? 'medium'}
					/>
				)}
			</Inline>
		</Stack>
	);
};
